import { memo, useEffect, useState } from "react";
import { Paperclip } from "lucide-react";

import { cn } from "@/lib/utils";
import type { AttachedFile } from "./types";

interface AttachedFileThumbnailProps {
  file: AttachedFile;
  /** Pixel size of the square thumbnail / fallback icon container. */
  size: number;
  /** Stroke width for the fallback paperclip icon. */
  iconStrokeWidth?: number;
  className?: string;
}

const isImage = (file: File) => file.type.startsWith("image/");

/**
 * Square preview for an attached file: shows a real image thumbnail when
 * the file is an image (managed via `URL.createObjectURL` and revoked on
 * unmount/file-change), and falls back to a paperclip icon otherwise.
 *
 * Caller controls the size; the wrapper is always `size x size`.
 */
export const AttachedFileThumbnail = memo(function AttachedFileThumbnail(props: AttachedFileThumbnailProps) {
  const { file, size, iconStrokeWidth = 1.6, className } = props;
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isImage(file.file)) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file.file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file.file]);

  const style = { width: size, height: size };

  if (previewUrl) {
    return (
      <img
        src={previewUrl}
        alt={file.file.name}
        style={style}
        className={cn("shrink-0 rounded-[3px] object-cover", className)}
      />
    );
  }

  const iconSize = Math.max(8, Math.round(size * 0.7));
  return (
    <span
      style={style}
      className={cn("inline-flex shrink-0 items-center justify-center", className)}
    >
      <Paperclip
        style={{ width: iconSize, height: iconSize }}
        strokeWidth={iconStrokeWidth}
      />
    </span>
  );
});
